import React, { useEffect, useState } from 'react';
import { fetchWithAuth } from '../utils/apiHelper';

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/D';
  const date = new Date(dateStr);
  return date.toLocaleString();
};

const GiftCardsAdmin = () => {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIndexes, setExpandedIndexes] = useState(new Set());
  
  // Estados para los filtros: fechas y estado
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stateFilter, setStateFilter] = useState('all'); // Valores: 'all', 'active', 'inactive'

  // Inicializar fechas con el rango del mes actual
  useEffect(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const formatDate = (date) => date.toISOString().split('T')[0];
    setStartDate(formatDate(startOfMonth));
    setEndDate(formatDate(endOfMonth));
  }, []);

  // Función para consultar el API con los filtros de fechas (el filtro por estado se aplica en el cliente)
  const fetchGiftCards = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `/giftcards/admin?start_date=${startDate}&end_date=${endDate}`;
      const data = await fetchWithAuth(url);
      setGiftCards(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Disparar la búsqueda
  const handleSearch = (e) => {
    e.preventDefault();
    fetchGiftCards();
  };

  // Filtrar las tarjetas según el estado seleccionado
  const filteredGiftCards = giftCards.filter((gc) => {
    if (stateFilter === 'all') return true;
    return gc.state === stateFilter;
  });

  // Cálculos de totales a partir de las tarjetas filtradas
  const totalTarjetas = filteredGiftCards.length;
  const totalValorInicial = filteredGiftCards.reduce((sum, gc) => sum + gc.initial_value, 0);
  const totalSaldoActual = filteredGiftCards.reduce((sum, gc) => sum + gc.current_balance, 0);
  const totalMontoUtilizado = totalValorInicial - totalSaldoActual;
  const totalMontoNoUtilizado = totalSaldoActual;

  const toggleExpand = (index) => {
    setExpandedIndexes((prev) => {
      const newSet = new Set(prev);
      newSet.has(index) ? newSet.delete(index) : newSet.add(index);
      return newSet;
    });
  };

  // Función para traducir el valor del filtro al mostrarlo
  const displayStateFilter = () => {
    if (stateFilter === 'all') return 'Todas';
    if (stateFilter === 'active') return 'Activas';
    if (stateFilter === 'inactive') return 'Inactivas';
    return stateFilter;
  };

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto">
      <h1 className="text-4xl font-bold text-gray-900 mb-4">🎁 Panel de Tarjetas de Regalo</h1>

      {/* Filtros */}
      <form
        onSubmit={handleSearch}
        className="mb-8 p-4 bg-white rounded-xl shadow-md grid grid-cols-1 sm:grid-cols-4 gap-6 border border-gray-200"
      >
        <div className="relative">
          <label className="absolute -top-3 left-2 bg-white px-1 text-sm text-gray-600">
            Fecha de inicio
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition"
          />
        </div>
        <div className="relative">
          <label className="absolute -top-3 left-2 bg-white px-1 text-sm text-gray-600">
            Fecha de fin
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition"
          />
        </div>
        <div className="relative">
          <label className="absolute -top-3 left-2 bg-white px-1 text-sm text-gray-600">
            Estado
          </label>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="mt-2 block w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base transition"
          >
            <option value="all">Todas</option>
            <option value="active">Activas</option>
            <option value="inactive">Inactivas</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <i className="fas fa-search mr-2"></i> Buscar
          </button>
        </div>
      </form>

      <p className="text-lg text-gray-600 mb-6">
        Mostrando tarjetas emitidas desde{' '}
        <span className="font-semibold text-gray-800">{startDate}</span> hasta{' '}
        <span className="font-semibold text-gray-800">{endDate}</span>{' '}
        {stateFilter !== 'all' && `- Filtro: ${displayStateFilter()}`}
      </p>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition transform hover:scale-105">
          <h2 className="text-gray-500 text-sm uppercase mb-2 tracking-wide">
            Total de Tarjetas
          </h2>
          <p className="text-3xl font-bold text-blue-600">{totalTarjetas}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition transform hover:scale-105">
          <h2 className="text-gray-500 text-sm uppercase mb-2 tracking-wide">
            Valor Inicial Acumulado
          </h2>
          <p className="text-3xl font-bold text-indigo-600">
            ${totalValorInicial.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition transform hover:scale-105">
          <h2 className="text-gray-500 text-sm uppercase mb-2 tracking-wide">
            Monto Utilizado
          </h2>
          <p className="text-3xl font-bold text-red-600">
            ${totalMontoUtilizado.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition transform hover:scale-105">
          <h2 className="text-gray-500 text-sm uppercase mb-2 tracking-wide">
            Monto No Utilizado
          </h2>
          <p className="text-3xl font-bold text-purple-600">
            ${totalMontoNoUtilizado.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {filteredGiftCards.length === 0 && !loading && (
        <div className="bg-yellow-50 text-yellow-800 p-4 rounded-lg mb-6 border border-yellow-300 text-center">
          <i className="fas fa-info-circle mr-2"></i>
          No se encontraron tarjetas para los filtros seleccionados.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-6 border border-red-300">
          <strong>Error:</strong> {error}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-lg rounded-xl">
          <table className="min-w-full text-base">
            <thead className="bg-blue-700 text-white sticky top-0 z-10 shadow">
              <tr>
                <th className="px-6 py-4 text-left">Código Público</th>
                <th className="px-6 py-4 text-right">Valor Inicial</th>
                <th className="px-6 py-4 text-right">Saldo</th>
                <th className="px-6 py-4 text-left">Estado</th>
                <th className="px-6 py-4 text-left">Emitida</th>
                <th className="px-6 py-4 text-left">Expira</th>
                <th className="px-6 py-4 text-left">Cliente</th>
                <th className="px-6 py-4 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-base">
              {filteredGiftCards.map((gc, index) => (
                <React.Fragment key={index}>
                  <tr className="hover:bg-gray-100 transition odd:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-800">{gc.public_code}</td>
                    <td className="px-6 py-4 text-right text-gray-700">
                      ${gc.initial_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-right text-gray-700">
                      ${gc.current_balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-3 py-1 text-sm rounded-full font-medium ${
                          gc.state === 'active'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {gc.state === 'active' ? (
                          <>
                            <i className="fas fa-check-circle mr-1"></i> Activa
                          </>
                        ) : (
                          <>
                            <i className="fas fa-times-circle mr-1"></i> Inactiva
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{formatDateTime(gc.issued_at)}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {gc.expires_at ? new Date(gc.expires_at).toLocaleDateString() : 'N/D'}
                    </td>
                    <td className="px-6 py-4 text-gray-800">
                      {gc.cliente_nombre ? `${gc.cliente_nombre} ${gc.cliente_apellido}` : 'Sin Cliente'}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => toggleExpand(index)}
                        className="text-blue-600 hover:text-blue-800 font-medium transition"
                      >
                        {expandedIndexes.has(index) ? 'Ocultar transacciones' : 'Ver transacciones'}
                      </button>
                    </td>
                  </tr>
                  {expandedIndexes.has(index) && (
                    <tr>
                      <td colSpan="8" className="px-6 py-6 bg-gray-50">
                        <div className="transition-all duration-300 transform scale-100">
                          <h3 className="text-lg font-semibold mb-3 text-gray-800 border-b pb-2">
                            Transacciones
                          </h3>
                          {gc.transactions && gc.transactions.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm border border-gray-200 rounded-lg">
                                <thead className="bg-gray-200 text-gray-700">
                                  <tr>
                                    <th className="px-4 py-2 text-left">Tipo</th>
                                    <th className="px-4 py-2 text-right">Monto</th>
                                    <th className="px-4 py-2 text-left">Fecha</th>
                                    <th className="px-4 py-2 text-left">Usuario</th>
                                    <th className="px-4 py-2 text-left">Comentario</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-300">
                                  {gc.transactions.map((tx, txIndex) => (
                                    <tr key={txIndex}>
                                      <td className="px-4 py-2">{tx.transaction_type}</td>
                                      <td className="px-4 py-2 text-right">
                                        ${tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                      </td>
                                      <td className="px-4 py-2">{formatDateTime(tx.transaction_date)}</td>
                                      <td className="px-4 py-2">{tx.usuario || 'N/D'}</td>
                                      <td className="px-4 py-2">{tx.comment || 'N/D'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-gray-600">No hay transacciones disponibles.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GiftCardsAdmin;
